import React, { useEffect, useState } from "react";
import { Button, Modal } from "antd";
import { UserAddOutlined, UserDeleteOutlined } from "@ant-design/icons";
import axios from "axios";
import { message } from "@bindeleddet/core/utils";
import { CompanyFollower } from "@bindeleddet/core/types";
import { isCompanyUser, useLoginContext } from "@bindeleddet/core/contexts/login";

interface Props {
  companyId?: number;
  companyName?: string;
  displayCompanyName?: boolean;
  block?: boolean;
  size?: "small" | "middle" | "large";
  withConfirm?: boolean;
  style?: React.CSSProperties;
}

const FollowCompanyButton: React.FC<Props> = ({
  companyId,
  companyName,
  displayCompanyName = false,
  block = true,
  size = "large",
  withConfirm = true,
  style,
}) => {
  const { currentUser } = useLoginContext();
  const [isFollowing, setIsFollowing] = useState(false);
  const [followerId, setFollowerId] = useState<number | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!currentUser || isCompanyUser(currentUser) || !companyId) {
      setLoaded(true);
      setLoading(false);
      return;
    }

    setLoading(true);
    axios
      .get<{ is_following: boolean; follower_id?: number }>(`/companies/${companyId}/followers/me/`)
      .then(response => {
        setIsFollowing(response.data?.is_following ?? false);
        setFollowerId(response.data?.follower_id ?? null);
      })
      .catch(() => {
        setIsFollowing(false);
        setFollowerId(null);
      })
      .finally(() => {
        setLoaded(true);
        setLoading(false);
      });
  }, [companyId, currentUser]);

  const follow = () => {
    if (!companyId || !currentUser || isCompanyUser(currentUser)) return;
    setLoading(true);
    axios
      .post<CompanyFollower>(`/companies/${companyId}/followers/`)
      .then(response => {
        setIsFollowing(true);
        setFollowerId(response.data.id);
        message("success", `Du følger ${companyName || "bedriften"}`);
      })
      .catch(() => {
        message("error", "Kunne ikke starte å følge bedriften");
      })
      .finally(() => setLoading(false));
  };

  const unfollow = () => {
    if (!companyId || !followerId) return;
    setLoading(true);
    axios
      .delete(`/companies/${companyId}/followers/${followerId}/`)
      .then(() => {
        setIsFollowing(false);
        setFollowerId(null);
        message("success", `Du følger ikke ${companyName || "bedriften"} lenger`);
      })
      .catch(() => {
        message("error", "Kunne ikke avfølge bedriften");
      })
      .finally(() => setLoading(false));
  };

  const handleFollowClick = () => {
    if (withConfirm && !isFollowing) {
      Modal.confirm({
        title: `Vil du begynne å følge ${companyName || "bedriften"}?`,
        content:
          "Du vil få tilsendt epost og push-varsel i appen når bedriften legger ut en ny stillingsannonse som er relevant for ditt årskull.",
        okText: `Følg ${companyName || "bedriften"}`,
        cancelText: "Avbryt",
        onOk: () => follow(),
      });
      return;
    }
    if (isFollowing) {
      unfollow();
    } else {
      follow();
    }
  };

  if (!loaded || !currentUser || isCompanyUser(currentUser) || !companyId) return null;

  return (
    <Button
      block={block}
      size={size}
      type="default"
      icon={isFollowing ? <UserDeleteOutlined /> : <UserAddOutlined />}
      loading={loading}
      disabled={loading}
      onClick={handleFollowClick}
      style={style}
    >
      {isFollowing ? "Avfølg" : "Følg"} {displayCompanyName ? companyName : ""}
    </Button>
  );
};

export default FollowCompanyButton;
